import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Download, Save, Calculator, Scan, X, CheckCircle, ClipboardList, CalendarCheck, AlertCircle, Pencil, Trash2, Plus } from "lucide-react";
import CameraScanner from './CameraScanner';
import { CustomerSearchSelect, ItemSearchSelect } from './CommonComponents';


const DEFAULT_ITEMS = ["LPG 20kg", "LPG 50kg", "산소(O2)", "질소(N2)", "아르곤(Ar)"];

const EMPTY_FORM = {
  customer: "",
  price: 0,
  targetWarehouse: "제1 창고(메인)",
  date: new Date().toISOString().split('T')[0],
  memo: "",
};

const Collection = ({ customers = [], cylinders = [], setCylinders, collections = [], setCollections, warehouses = [], items = [] }) => {
  const itemNames = useMemo(() => {
    const fromSettings = items.length > 0 ? items.map(i => i.name) : DEFAULT_ITEMS;
    const fromCylinders = cylinders.map(c => c.item).filter(Boolean);
    return [...new Set([...fromSettings, ...fromCylinders])];
  }, [items, cylinders]);
  const itemMap = useMemo(() =>
    Object.fromEntries(itemNames.map(n => [`${n} (공병)`, n])),
  [itemNames]);

  const [scanMode, setScanMode]         = useState(false);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scannedList, setScannedList]   = useState([]);
  const [scanError, setScanError]       = useState('');
  const [formData, setFormData]         = useState(() => ({ ...EMPTY_FORM, targetWarehouse: warehouses[0] ?? EMPTY_FORM.targetWarehouse }));
  const [itemRows, setItemRows]         = useState(() => [{ item: `${itemNames[0] ?? ""} (공병)`, quantity: 0, price: 0 }]);
  const [formError, setFormError]       = useState('');
  const [saved, setSaved]               = useState(false);
  const barcodeRef = useRef(null);

  // 이력 필터
  const [filterYear,     setFilterYear]     = useState('');
  const [filterMonth,    setFilterMonth]    = useState('');
  const [filterDay,      setFilterDay]      = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');

  // 존재하는 연도 목록
  const availableYears = useMemo(() => {
    const ys = [...new Set(collections.map(d => d.date?.slice(0, 4)).filter(Boolean))].sort().reverse();
    return ys;
  }, [collections]);

  const filteredCollections = useMemo(() => {
    return [...collections]
      .reverse()
      .filter(d => {
        if (!d.date) return true;
        if (filterYear  && !d.date.startsWith(filterYear))  return false;
        if (filterMonth && !d.date.startsWith(`${filterYear}-${filterMonth}`)) return false;
        if (filterDay   && d.date !== `${filterYear}-${filterMonth}-${filterDay}`) return false;
        if (filterCustomer && d.customer !== filterCustomer) return false;
        return true;
      });
  }, [collections, filterYear, filterMonth, filterDay, filterCustomer]);

  const filteredTotal = useMemo(() =>
    filteredCollections.reduce((sum, d) => sum + (d.amount || 0), 0),
    [filteredCollections]
  );

  // 이력 수정/삭제
  const [editingId, setEditingId]           = useState(null);
  const [editForm, setEditForm]             = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditForm({ date: d.date, customer: d.customer, price: d.price ?? 0, memo: d.memo ?? '' });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const saveEdit = (d) => {
    const newPrice  = parseInt(editForm.price) || 0;
    const newAmount = d.quantity * newPrice;
    if (editForm.customer !== d.customer && d.cylinders?.length) {
      setCylinders(prev => prev.map(c =>
        d.cylinders.includes(c.id) ? { ...c, location: editForm.customer } : c
      ));
    }
    setCollections(prev => prev.map(r =>
      r.id === d.id
        ? { ...r, date: editForm.date, customer: editForm.customer, price: newPrice, amount: newAmount, memo: editForm.memo }
        : r
    ));
    setEditingId(null);
    setEditForm({});
  };

  const deleteCollection = (d) => {
    if (d.cylinders?.length) {
      setCylinders(prev => prev.map(c =>
        d.cylinders.includes(c.id)
          ? { ...c, locType: 'customer', location: d.customer, status: '공병' }
          : c
      ));
    }
    setCollections(prev => prev.filter(r => r.id !== d.id));
    setConfirmDeleteId(null);
  };

  useEffect(() => {
    if (scanMode && barcodeRef.current) barcodeRef.current.focus();
  }, [scanMode]);

  const scannedQuantity = scannedList.length;
  const itemCounts = scannedList.reduce((acc, s) => {
    acc[s.item] = (acc[s.item] || 0) + 1;
    return acc;
  }, {});

  const availableAtCustomer = (customer, displayItem) => {
    const baseItem = itemMap[displayItem] || displayItem;
    return cylinders.filter(
      c => c.locType === 'customer' && c.location === customer && c.item === baseItem
    ).length;
  };

  const processBarcode = (code) => {
    if (!code) return;
    setScanError('');
    if (scannedList.find(s => s.id === code)) {
      setScanError(`이미 스캔된 용기입니다: ${code}`); return;
    }
    const cyl = cylinders.find(c => c.id === code);
    const item = cyl?.item;
    if (!item) { setScanError(`등록되지 않은 바코드: ${code}`); return; }
    // 거래처 외 위치여도 허용 (음수 재고 지원) — 경고만 표시
    const notAtCustomer = cyl.locType !== 'customer';
    setScannedList(prev => [...prev, { id: code, item, notAtCustomer }]);
  };

  const handleBarcodeKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      processBarcode(barcodeInput.trim());
      setBarcodeInput('');
    }
  };

  // 다중 품목 행 헬퍼
  const updateRow = (idx, field, value) => {
    setItemRows(prev => prev.map((r, i) => i !== idx ? r : { ...r, [field]: value }));
  };

  const addRow = () => {
    const firstDisplayItem = `${itemNames[0] ?? ""} (공병)`;
    setItemRows(prev => [...prev, { item: firstDisplayItem, quantity: 0, price: 0 }]);
  };

  const removeRow = (idx) => {
    setItemRows(prev => prev.filter((_, i) => i !== idx));
  };

  const normalTotalAmount = itemRows.reduce((sum, r) => sum + (r.quantity || 0) * (r.price || 0), 0);
  const scanTotalAmount   = scannedQuantity * (formData.price || 0);
  const totalAmount       = scanMode ? scanTotalAmount : normalTotalAmount;

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!formData.customer) { setFormError('거래처를 선택하세요.'); return; }

    let record;
    let returnedIds = [];

    if (scanMode) {
      if (scannedQuantity === 0) { setFormError('용기를 스캔하세요.'); return; }
      returnedIds = scannedList.map(s => s.id);
      setCylinders(prev => prev.map(c =>
        returnedIds.includes(c.id)
          ? { ...c, locType: 'warehouse', location: formData.targetWarehouse, status: '공병' }
          : c
      ));
      record = {
        id: Date.now(),
        customer: formData.customer,
        item: Object.keys(itemCounts).length === 1 ? Object.keys(itemCounts)[0] : '혼합',
        items: { ...itemCounts },
        quantity: scannedQuantity,
        price: formData.price,
        amount: scanTotalAmount,
        date: formData.date,
        memo: formData.memo || '',
        targetWarehouse: formData.targetWarehouse,
        cylinders: returnedIds,
      };
    } else {
      const validRows = itemRows.filter(r => (r.quantity || 0) > 0);
      if (validRows.length === 0) { setFormError('수량을 1개 이상 입력하세요.'); return; }

      for (const row of validRows) {
        const baseItem = itemMap[row.item] || row.item;
        const atCustomer = cylinders.filter(
          c => c.locType === 'customer' && c.location === formData.customer && c.item === baseItem
        );
        // 추적 가능한 용기만 창고로 이동 (수량 부족해도 음수 재고로 기록 허용)
        const rowIds = atCustomer.slice(0, row.quantity).map(c => c.id);
        returnedIds = [...returnedIds, ...rowIds];
      }
      setCylinders(prev => prev.map(c =>
        returnedIds.includes(c.id)
          ? { ...c, locType: 'warehouse', location: formData.targetWarehouse, status: '공병' }
          : c
      ));

      const itemsMap = {};
      let totalQty = 0;
      let totalAmt = 0;
      for (const row of validRows) {
        itemsMap[row.item] = (itemsMap[row.item] || 0) + row.quantity;
        totalQty += row.quantity;
        totalAmt += row.quantity * (row.price || 0);
      }

      record = {
        id: Date.now(),
        customer: formData.customer,
        item: Object.keys(itemsMap).length === 1 ? Object.keys(itemsMap)[0] : '혼합',
        items: itemsMap,
        quantity: totalQty,
        price: 0,
        amount: totalAmt,
        date: formData.date,
        memo: formData.memo || '',
        targetWarehouse: formData.targetWarehouse,
        cylinders: returnedIds,
      };
    }

    setCollections(prev => [...prev, record]);
    setFormData(prev => ({ ...EMPTY_FORM, targetWarehouse: prev.targetWarehouse }));
    setItemRows([{ item: `${itemNames[0] ?? ""} (공병)`, quantity: 0, price: 0 }]);
    setScannedList([]);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* 등록 폼 */}
      <div style={sectionBox}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 25 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#059669" }}>
            <Download size={24} />
            <h2 style={{ margin: 0, fontSize: 20 }}>매입(회수) 상세 등록</h2>
          </div>
          <div style={{ display: 'flex', background: '#f0f0f0', padding: 3, borderRadius: 8, gap: 2 }}>
            <button type="button" onClick={() => { setScanMode(false); setScanError(''); setFormError(''); }}
              style={{ ...modeBtn, background: !scanMode ? '#fff' : 'transparent', color: !scanMode ? '#059669' : '#888', fontWeight: !scanMode ? 700 : 400 }}>
              일반 입력
            </button>
            <button type="button" onClick={() => { setScanMode(true); setFormError(''); }}
              style={{ ...modeBtn, background: scanMode ? '#059669' : 'transparent', color: scanMode ? '#fff' : '#888', fontWeight: scanMode ? 700 : 400 }}>
              <Scan size={13} /> 바코드 스캔
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* 바코드 스캔 영역 */}
          {scanMode && (
            <div style={{ background: '#f0fdf8', border: '1px solid #05966944', borderRadius: 12, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Scan size={15} color="#059669" />
                <span style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>바코드 입력</span>
                <span style={{ fontSize: 12, color: '#999' }}>직접 입력 후 Enter · 추가 · 카메라 스캔</span>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: scanError ? 8 : 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <input ref={barcodeRef} value={barcodeInput}
                  onChange={e => { setBarcodeInput(e.target.value); setScanError(''); }}
                  onKeyDown={handleBarcodeKeyDown}
                  placeholder="바코드를 스캔하거나 직접 입력 후 Enter"
                  style={{ ...inputStyle, flex: 1, minWidth: 160, background: '#fff', fontFamily: 'monospace' }} />
                <button type="button"
                  onClick={() => { processBarcode(barcodeInput.trim()); setBarcodeInput(''); barcodeRef.current?.focus(); }}
                  style={{ padding: '10px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  추가
                </button>
                <CameraScanner
                  color="#059669"
                  onScan={(code) => { processBarcode(code); barcodeRef.current?.focus(); }}
                />
              </div>
              {scanError && <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#DC2626', fontWeight: 600 }}>{scanError}</p>}
              {scannedList.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>스캔된 용기 <span style={{ color: '#059669' }}>{scannedList.length}개</span></span>
                    <button type="button" onClick={() => setScannedList([])}
                      style={{ fontSize: 12, color: '#DC2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>전체 초기화</button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 180, overflowY: 'auto', marginBottom: 10 }}>
                    {scannedList.map((s, i) => (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', background: '#fff', padding: '7px 12px', borderRadius: 8, border: `1px solid ${s.notAtCustomer ? '#fde68a' : '#cceedd'}`, gap: 10 }}>
                        <CheckCircle size={13} color={s.notAtCustomer ? '#D97706' : '#059669'} />
                        <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700, flex: 1, color: s.notAtCustomer ? '#92400E' : '#1e293b' }}>{s.id}</span>
                        <span style={{ fontSize: 12, color: '#555', background: s.notAtCustomer ? '#fef3c7' : '#e8faf2', padding: '2px 8px', borderRadius: 10 }}>{s.item}</span>
                        {s.notAtCustomer && (
                          <span style={{ fontSize: 11, color: '#D97706', fontWeight: 700, background: '#fef3c7', padding: '1px 6px', borderRadius: 6, whiteSpace: 'nowrap' }}>위치 불일치</span>
                        )}
                        <button type="button" onClick={() => setScannedList(prev => prev.filter((_, idx) => idx !== i))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', padding: 2, display: 'flex' }}>
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '8px 14px', background: '#fff', borderRadius: 8, border: '1px solid #cceedd', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {Object.entries(itemCounts).map(([item, cnt]) => (
                      <span key={item} style={{ fontSize: 13 }}>
                        <span style={{ fontWeight: 700, color: '#059669' }}>{item}</span>
                        <span style={{ color: '#555' }}> {cnt}본</span>
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', padding: '16px 0', color: '#bbb', fontSize: 13 }}>스캔된 용기가 없습니다</div>
              )}
            </div>
          )}

          {/* 공통 상단 필드: 거래처 · 입고창고 · 날짜 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 20 }}>
            <div>
              <label style={labelStyle}>회수처(거래처)</label>
              <CustomerSearchSelect
                customers={customers}
                value={formData.customer}
                onChange={name => { setFormData(prev => ({ ...prev, customer: name })); setFormError(''); }}
                color="#059669"
              />
            </div>
            <div>
              <label style={labelStyle}>입고 창고</label>
              <select style={inputStyle} value={formData.targetWarehouse} onChange={e => setFormData({...formData, targetWarehouse: e.target.value})}>
                {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>회수 일자</label>
              <input type="date" style={inputStyle} value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
            </div>
          </div>

          {/* 일반 입력: 다중 품목 행 */}
          {!scanMode && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <label style={labelStyle}>품목별 수량 / 단가</label>
                <button type="button" onClick={addRow} style={addRowBtn}>
                  <Plus size={13} /> 품목 추가
                </button>
              </div>

              {/* 헤더 */}
              <div style={rowHeaderStyle}>
                <span style={{ gridColumn: '1' }}>품목(공병)</span>
                <span style={{ gridColumn: '2', textAlign: 'center' }}>거래처 보유</span>
                <span style={{ gridColumn: '3', textAlign: 'center' }}>수량(본)</span>
                <span style={{ gridColumn: '4', textAlign: 'center' }}>단가(원)</span>
                <span style={{ gridColumn: '5', textAlign: 'right' }}>소계</span>
                <span style={{ gridColumn: '6' }}></span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {itemRows.map((row, idx) => {
                  const atCustomer = formData.customer ? availableAtCustomer(formData.customer, row.item) : null;
                  const overCollection = atCustomer !== null && (row.quantity || 0) > atCustomer;
                  const subtotal = (row.quantity || 0) * (row.price || 0);
                  return (
                    <div key={idx} style={rowGridStyle}>
                      <ItemSearchSelect
                        itemNames={itemNames.map(n => `${n} (공병)`)}
                        value={row.item}
                        onChange={name => updateRow(idx, 'item', name)}
                        placeholder="품목 검색"
                        color="#059669"
                      />
                      <div style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, alignSelf: 'center',
                        color: atCustomer === null ? '#aaa' : atCustomer === 0 ? '#888' : '#059669' }}>
                        {atCustomer === null ? '-' : `${atCustomer}개`}
                        {overCollection && (
                          <div style={{ fontSize: 10, color: '#D97706', fontWeight: 600, marginTop: 1 }}>음수재고↓</div>
                        )}
                      </div>
                      <input type="number" min={0}
                        style={{ ...inputStyle, padding: '9px 10px', fontSize: 13, textAlign: 'center' }}
                        value={row.quantity || ''}
                        onChange={e => updateRow(idx, 'quantity', parseInt(e.target.value || 0))} />
                      <input type="number" min={0}
                        style={{ ...inputStyle, padding: '9px 10px', fontSize: 13, textAlign: 'right' }}
                        value={row.price || ''}
                        onChange={e => updateRow(idx, 'price', parseInt(e.target.value || 0))} />
                      <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: subtotal > 0 ? '#059669' : '#bbb', alignSelf: 'center' }}>
                        {subtotal > 0 ? subtotal.toLocaleString() + '원' : '-'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {itemRows.length > 1 && (
                          <button type="button" onClick={() => removeRow(idx)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: 4, display: 'flex', alignItems: 'center' }}>
                            <X size={15} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 바코드 스캔 모드: 수량·단가 */}
          {scanMode && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 15 }}>
              <div>
                <label style={labelStyle}>수량 (자동집계)</label>
                <input type="number"
                  style={{ ...inputStyle, background: '#e8faf2', color: '#059669', fontWeight: 700 }}
                  value={scannedQuantity} readOnly />
              </div>
              <div>
                <label style={labelStyle}>매입 단가 (원)</label>
                <input type="number" style={inputStyle} placeholder="0" value={formData.price || ''}
                  onChange={e => setFormData({...formData, price: parseInt(e.target.value || 0)})} />
              </div>
            </div>
          )}

          <div>
            <label style={labelStyle}>비고</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 60, fontSize: 14 }}
              placeholder="특이사항, 메모 등 (선택사항)"
              value={formData.memo || ''}
              onChange={e => setFormData({...formData, memo: e.target.value})}
            />
          </div>

          {/* 합계 카드 */}
          <div style={amountCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#059669' }}>
              <Calculator size={16} /> <span>계산된 매입 합계</span>
            </div>
            {!scanMode && itemRows.filter(r => (r.quantity || 0) > 0).length > 1 && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {itemRows.filter(r => (r.quantity || 0) > 0).map((r, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#555' }}>
                    <span>{r.item} {r.quantity}본 × {(r.price || 0).toLocaleString()}원</span>
                    <span style={{ fontWeight: 600 }}>{((r.quantity || 0) * (r.price || 0)).toLocaleString()}원</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid #05966933', marginTop: 4, paddingTop: 4 }}></div>
              </div>
            )}
            <div style={{ fontSize: 24, fontWeight: 800, color: '#059669', marginTop: 10 }}>
              {totalAmount.toLocaleString()} <span style={{ fontSize: 16, fontWeight: 400, color: '#666' }}>원</span>
            </div>
          </div>

          {formError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
              <AlertCircle size={15} color="#DC2626" />
              <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>{formError}</span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <button type="submit" style={{ ...submitBtn, flex: 1 }}>
              <Save size={18} /> 매입 내역 저장 (용기 창고 복귀)
            </button>
            {saved && <span style={{ fontSize: 13, color: '#059669', fontWeight: 700, whiteSpace: 'nowrap' }}>✓ 저장 완료!</span>}
          </div>
        </form>
      </div>

      {/* 매입 이력 */}
      <div style={sectionBox}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <ClipboardList size={18} color="#059669" />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>매입 이력</h3>
          <span style={{ fontSize: 13, color: '#888' }}>총 {collections.length}건</span>
        </div>

        {/* 필터 */}
        {collections.length > 0 && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center', padding: '12px 14px', background: '#f0fdf8', borderRadius: 10, border: '1px solid #d1fae5' }}>
            <div>
              <label style={filterLabelStyle}>년도</label>
              <select style={filterInputStyle} value={filterYear}
                onChange={e => { setFilterYear(e.target.value); setFilterMonth(''); setFilterDay(''); setEditingId(null); }}>
                <option value="">전체</option>
                {availableYears.map(y => <option key={y} value={y}>{y}년</option>)}
              </select>
            </div>
            <div>
              <label style={filterLabelStyle}>월</label>
              <select style={filterInputStyle} value={filterMonth} disabled={!filterYear}
                onChange={e => { setFilterMonth(e.target.value); setFilterDay(''); setEditingId(null); }}>
                <option value="">전체</option>
                {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                  <option key={m} value={m}>{parseInt(m)}월</option>
                ))}
              </select>
            </div>
            <div>
              <label style={filterLabelStyle}>일</label>
              <select style={filterInputStyle} value={filterDay} disabled={!filterMonth}
                onChange={e => { setFilterDay(e.target.value); setEditingId(null); }}>
                <option value="">전체</option>
                {[...Array(31)].map((_, i) => String(i + 1).padStart(2, '0')).map(d => (
                  <option key={d} value={d}>{parseInt(d)}일</option>
                ))}
              </select>
            </div>
            <div style={{ minWidth: 160 }}>
              <label style={filterLabelStyle}>거래처</label>
              <select style={filterInputStyle}
                value={filterCustomer}
                onChange={e => { setFilterCustomer(e.target.value); setEditingId(null); }}>
                <option value="">전체 거래처</option>
                {[...new Set(collections.map(d => d.customer))].sort().map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            {(filterYear || filterCustomer) && (
              <button onClick={() => { setFilterYear(''); setFilterMonth(''); setFilterDay(''); setFilterCustomer(''); }}
                style={{ alignSelf: 'flex-end', padding: '7px 12px', background: 'none', border: '1px solid #d1fae5', borderRadius: 7, fontSize: 12, color: '#666', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <X size={12} /> 필터 초기화
              </button>
            )}
            {(filterYear || filterCustomer) && (
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 12, color: '#888' }}>{filteredCollections.length}건 / 합계</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>{filteredTotal.toLocaleString()}원</div>
              </div>
            )}
          </div>
        )}

        {collections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10 }}>
            등록된 매입 내역이 없습니다
          </div>
        ) : filteredCollections.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: '#bbb', fontSize: 13, border: '1px dashed #e5e3db', borderRadius: 10 }}>
            검색 결과가 없습니다
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredCollections.map(d => {
              const isService = d.type === 'service';
              return (
                <div key={d.id} style={{ borderRadius: 10, border: editingId === d.id ? '1.5px solid #059669' : `1px solid ${isService ? '#ddd6fe' : '#d1fae5'}`, overflow: 'hidden' }}>
                  {editingId === d.id ? (
                    <div style={{ padding: '14px 16px', background: '#f0fdf8', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label style={editLabelStyle}>날짜</label>
                          <input type="date" style={editInputStyle}
                            value={editForm.date}
                            onChange={e => setEditForm(prev => ({ ...prev, date: e.target.value }))} />
                        </div>
                        <div style={{ minWidth: 160 }}>
                          <label style={editLabelStyle}>거래처</label>
                          <CustomerSearchSelect
                            customers={customers}
                            value={editForm.customer}
                            onChange={name => setEditForm(prev => ({ ...prev, customer: name }))}
                            color="#059669"
                            inputStyle={{ padding: '8px 10px', border: '1px solid #6ee7b7', fontSize: 13, background: '#fff' }}
                          />
                        </div>
                        <div style={{ width: 120 }}>
                          <label style={editLabelStyle}>단가 (원)</label>
                          <input type="number" style={editInputStyle} min="0"
                            value={editForm.price}
                            onChange={e => setEditForm(prev => ({ ...prev, price: e.target.value }))} />
                        </div>
                        <div>
                          <label style={editLabelStyle}>합계</label>
                          <span style={{ display: 'block', padding: '8px 10px', fontSize: 14, fontWeight: 700, color: '#059669' }}>
                            {(d.quantity * (parseInt(editForm.price) || 0)).toLocaleString()}원
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {d.items
                          ? Object.entries(d.items).map(([item, cnt]) => (
                              <span key={item} style={{ fontSize: 12, background: '#d1fae5', color: '#059669', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{item} {cnt}본 (변경불가)</span>
                            ))
                          : <span style={{ fontSize: 12, color: '#888' }}>{d.item} {d.quantity}본 (변경불가)</span>
                        }
                      </div>
                      <div>
                        <label style={editLabelStyle}>비고</label>
                        <textarea
                          style={{ ...editInputStyle, width: '100%', resize: 'vertical', minHeight: 50, fontSize: 13, boxSizing: 'border-box' }}
                          placeholder="비고"
                          value={editForm.memo ?? ''}
                          onChange={e => setEditForm(prev => ({ ...prev, memo: e.target.value }))}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => saveEdit(d)} style={editSaveBtn}>
                          <Save size={14} /> 저장
                        </button>
                        <button onClick={cancelEdit} style={editCancelBtn}>
                          <X size={14} /> 취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                        background: isService ? '#f5f3ff' : '#f0fdf8', flexWrap: 'wrap' }}>
                        {isService
                          ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: '#7C3AED', color: '#fff', whiteSpace: 'nowrap' }}>서비스비용</span>
                          : <CalendarCheck size={14} color="#059669" />
                        }
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#333', minWidth: 90 }}>{d.date}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: isService ? '#7C3AED' : '#059669' }}>{d.customer}</span>
                        <div style={{ flex: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {d.items
                            ? Object.entries(d.items).map(([item, cnt]) => (
                                <span key={item} style={{ fontSize: 12,
                                  background: isService ? '#ede9fe' : '#e8faf2',
                                  color: isService ? '#7C3AED' : '#059669',
                                  padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                                  {item} {cnt}본
                                </span>
                              ))
                            : <span style={{ fontSize: 13, color: '#555' }}>{d.item} {d.quantity}본</span>
                          }
                        </div>
                        {d.targetWarehouse && (
                          <span style={{ fontSize: 12, color: '#777', background: '#fff', padding: '2px 10px', borderRadius: 8, border: `1px solid ${isService ? '#ddd6fe' : '#d1fae5'}` }}>
                            {d.targetWarehouse}
                          </span>
                        )}
                        <span style={{ fontSize: 14, fontWeight: 800, color: isService ? '#7C3AED' : '#059669', minWidth: 100, textAlign: 'right' }}>
                          {(d.amount || 0).toLocaleString()}원
                        </span>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button onClick={() => { setConfirmDeleteId(null); startEdit(d); }} style={editBtn} title="수정">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => { setEditingId(null); setConfirmDeleteId(d.id); }} style={deleteBtn} title="삭제">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      {d.memo && (
                        <div style={{ padding: '6px 16px 8px', background: isService ? '#f5f3ff' : '#f0fdf8', borderTop: '1px solid #e8f5ee', fontSize: 12, color: '#666' }}>
                          비고: {d.memo}
                        </div>
                      )}
                      {confirmDeleteId === d.id && (
                        <div style={{ padding: '10px 16px', background: '#fef2f2', borderTop: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, color: '#DC2626', fontWeight: 600 }}>이 매입 이력을 삭제하시겠습니까? (용기가 거래처로 복귀됩니다)</span>
                          <button onClick={() => deleteCollection(d)} style={{ padding: '6px 14px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>삭제 확인</button>
                          <button onClick={() => setConfirmDeleteId(null)} style={{ padding: '6px 12px', background: '#fff', color: '#666', border: '1px solid #d3d1c7', borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>취소</button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const rowGridStyle = { display: 'grid', gridTemplateColumns: '2fr 80px 90px 120px 110px 36px', gap: 8, alignItems: 'center' };
const rowHeaderStyle = { ...rowGridStyle, padding: '6px 10px', background: '#f0fdf8', borderRadius: 6, fontSize: 12, color: '#555', fontWeight: 600, marginBottom: 4, border: '1px solid #d1fae5' };
const addRowBtn     = { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: '#e8faf2', color: '#059669', border: '1px solid #05966933', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const modeBtn        = { border: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 };
const filterLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#888', marginBottom: 4 };
const filterInputStyle = { padding: '7px 10px', borderRadius: 7, border: '1px solid #d3d1c7', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const editBtn        = { border: '1px solid #d3d1c7', background: '#fff', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: '#059669', display: 'flex', alignItems: 'center', flexShrink: 0 };
const deleteBtn      = { border: '1px solid #fecaca', background: '#fff', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', color: '#DC2626', display: 'flex', alignItems: 'center', flexShrink: 0 };
const editLabelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 };
const editInputStyle = { padding: '8px 10px', borderRadius: 6, border: '1px solid #6ee7b7', fontSize: 13, background: '#fff', boxSizing: 'border-box' };
const editSaveBtn    = { display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', background: '#059669', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const editCancelBtn  = { display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', background: '#fff', color: '#666', border: '1px solid #d3d1c7', borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const labelStyle = { display: "block", fontSize: 13, fontWeight: 600, color: "#666", marginBottom: 8 };
const inputStyle = { width: "100%", padding: "12px", borderRadius: 8, border: "1px solid #d3d1c7", fontSize: 15, boxSizing: "border-box", background: "#fcfbf7" };
const amountCard = { padding: "20px", background: "#ecfdf5", borderRadius: 12, border: "1px solid #05966933", textAlign: "right" };
const submitBtn  = { padding: "15px", background: "#059669", color: "white", border: "none", borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 };
const sectionBox = { background: "white", padding: 30, borderRadius: 16, border: "1px solid #d3d1c7" };

export default Collection;
